import React, { FC, useMemo } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon } from "@mui/icons-material";
import { tokens } from "@/tokens";

interface MultiSelectFilterProps {
  label: string;
  options: Array<{ value: string; label: string; meta?: string }>;
  value: string[];
  onChange: (newValue: string[]) => void;
  placeholder: string;
  disableCloseOnSelect?: boolean;
  disablePortal?: boolean;
  open?: boolean;
  onOpen?: () => void;
  onClose?: (
    event: React.SyntheticEvent,
    reason: "toggleInput" | "escape" | "selectOption" | "removeOption" | "blur" | "createOption"
  ) => void;
}

const MultiSelectFilter: FC<MultiSelectFilterProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  disableCloseOnSelect = false,
  disablePortal = false,
  open,
  onOpen,
  onClose,
}) => {
  const selectedOptions = useMemo(() => {
    const optionMap = new Map(options.map((option) => [option.value, option]));
    return value.map((selected) => optionMap.get(selected) ?? { value: selected, label: selected });
  }, [options, value]);

  return (
    <Box>
      <Typography
        sx={{
          mb: 1,
          color: tokens.text.primary,
          fontWeight: tokens.fontWeight.semibold,
          fontSize: tokens.fontSize.base,
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        multiple
        size="small"
        options={options}
        value={selectedOptions}
        getOptionLabel={(option) => option.label || option.value}
        onChange={(_, newValue) => onChange(newValue.map((option) => option.value))}
        disableCloseOnSelect={disableCloseOnSelect || true}
        disablePortal={disablePortal}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        renderOption={(props, option, { selected }) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: "6px !important",
              px: "12px !important",
              borderBottom: `1px solid ${tokens.border.light}`,
              "&:last-child": { borderBottom: "none" },
              "&:hover": { bgcolor: `${tokens.bg.hover} !important` },
              ...(selected && { bgcolor: `${tokens.bg.hover} !important` }),
            }}
          >
            <Checkbox
              icon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
              checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
              checked={selected}
              size="small"
              sx={{
                p: 0,
                color: tokens.text.disabled,
                "&.Mui-checked": { color: tokens.brand.primary },
              }}
            />
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: selected ? tokens.fontWeight.semibold : tokens.fontWeight.normal, color: tokens.text.body, flex: 1 }}>
              {option.label || option.value}
            </Typography>
            {option.meta ? (
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontVariantNumeric: "tabular-nums" }}>
                {option.meta}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontFamily: "monospace" }}>
                {option.value}
              </Typography>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? placeholder : ""}
            sx={{
              backgroundColor: tokens.bg.surface,
              "& .MuiInputBase-input": { fontSize: tokens.fontSize.base },
              "& .MuiOutlinedInput-root": {
                borderRadius: tokens.radius.md,
              },
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label || option.value}
              size="small"
              sx={{
                height: 22,
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                bgcolor: `${tokens.brand.primary}14`,
                color: tokens.brand.primary,
                borderRadius: tokens.radius.sm,
                "& .MuiChip-deleteIcon": {
                  fontSize: tokens.fontSize.lg,
                  color: tokens.brand.primary,
                  "&:hover": { color: tokens.brand.primaryHover },
                },
              }}
            />
          ))
        }
        ListboxProps={{
          style: { maxHeight: 240, padding: 0 },
        }}
        sx={{
          "& .MuiAutocomplete-tag": {
            margin: "2px",
          },
        }}
      />
    </Box>
  );
};

export default MultiSelectFilter;
